<script setup lang="ts">
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { 
    mdiMagnify, 
    mdiLink, 
    mdiTelevisionClassic, 
    mdiPlaylistPlay, 
    mdiVideo,
    mdiClose
} from '@mdi/js';
import { storeToRefs } from 'pinia';
import { useSearchStore } from '../store/search';
import { SearchResult } from '../models/link';

const router = useRouter();
const searchStore = useSearchStore();
const { showSearch, searchResults } = storeToRefs(searchStore);
const searchTerm = ref('');
const isLoading = ref(false);
const selectedTypes = ref<string[]>(['channel', 'video', 'list', 'link']);

const availableTypes = [
    { id: 'channel', label: 'Channels', icon: mdiTelevisionClassic },
    { id: 'video', label: 'Videos', icon: mdiVideo },
    { id: 'list', label: 'Lists', icon: mdiPlaylistPlay },
    { id: 'link', label: 'Links', icon: mdiLink },
];

let debounceTimer: any = null;

watch([searchTerm, selectedTypes], ([newTerm, newTypes]) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    if (!newTerm) {
        searchStore.searchResults = [];
        return;
    }
    if (newTypes.length === 0) {
        searchStore.searchResults = [];
        return;
    }
    isLoading.value = true;
    debounceTimer = setTimeout(() => {
        searchStore.search(newTerm, newTypes).finally(() => {
            isLoading.value = false;
        });
    }, 300);
}, { deep: true });

function getIcon(type: string) {
    switch (type) {
        case 'channel': return mdiTelevisionClassic;
        case 'video': return mdiVideo;
        case 'list': return mdiPlaylistPlay;
        case 'link': return mdiLink;
        default: return mdiLink;
    }
}

function handleResultClick(result: SearchResult) {
    showSearch.value = false;
    searchTerm.value = '';
    
    switch (result.type) {
        case 'channel':
            router.push({ name: 'channel', params: { channelId: result.id } });
            break;
        case 'video':
            router.push({ name: 'video', params: { channelId: result.channel_id || '0', videoId: result.id } });
            break;
        case 'list':
            router.push({ name: 'list', params: { channelId: result.channel_id || '0', listId: result.id } });
            break;
        case 'link':
            // For links, go to the link detail if possible, or just open?
            // Existing routes indicate /channels/:channelId/links/:linkId exists
            router.push({ name: 'link', params: { channelId: result.channel_id || '0', linkId: result.id } });
            break;
    }
}
</script>

<template>
    <v-overlay v-model="showSearch" class="align-start justify-center pt-16 search-overlay" contained>
        <div class="search-wrapper">
            <v-card class="search-card" elevation="24">
                <v-text-field
                    v-model="searchTerm"
                    :loading="isLoading"
                    :prepend-inner-icon="mdiMagnify"
                    :append-inner-icon="mdiClose"
                    autofocus
                    variant="filled"
                    hide-details
                    placeholder="Search links, channels, videos, lists..."
                    @click:append-inner="showSearch = false"
                    @keyup.esc="showSearch = false"
                    class="search-field"
                >
                </v-text-field>

                <div class="px-4 py-2 d-flex flex-wrap align-center filter-row">
                    <span class="text-caption text-medium-emphasis mr-2">Filter:</span>
                    <v-chip-group
                        v-model="selectedTypes"
                        column
                        multiple
                        selected-class="bg-primary text-white filter-chip--selected"
                    >
                        <v-chip
                            v-for="type in availableTypes"
                            :key="type.id"
                            :value="type.id"
                            size="small"
                            variant="outlined"
                            class="ma-1 filter-chip"
                        >
                            <v-icon :icon="type.icon" start size="16"></v-icon>
                            {{ type.label }}
                        </v-chip>
                    </v-chip-group>
                </div>

                <v-divider></v-divider>

                <div v-if="selectedTypes.length === 0" class="pa-8 text-center text-grey">
                    Please select at least one type to search.
                </div>
                <div v-else-if="searchResults.length > 0" class="results-container">
                    <v-list lines="two" class="py-0">
                        <v-list-item
                            v-for="result in searchResults"
                            :key="result.id + result.type"
                            :title="result.name || result.url"
                            :subtitle="result.type === 'channel' ? 'Channel' : result.channel_name"
                            @click="handleResultClick(result)"
                            class="result-item"
                        >
                            <template v-slot:prepend>
                                <v-avatar 
                                    v-if="result.image_url || result.channel_image_url || (result.type === 'link' && result.domain)" 
                                    :image="result.image_url || (result.type === 'link' && result.domain ? `https://www.google.com/s2/favicons?sz=64&domain=${result.domain}` : result.channel_image_url)" 
                                    size="40" rounded="sm" class="mr-3"
                                ></v-avatar>
                                <v-avatar v-else color="grey-lighten-4" size="40" rounded="sm" class="mr-3 text-grey-darken-1">
                                    <v-icon :icon="getIcon(result.type)"></v-icon>
                                </v-avatar>
                            </template>
                            
                            <template v-slot:append>
                                <v-chip size="x-small" :color="result.type === 'link' ? 'primary' : 'secondary'" variant="tonal" class="text-capitalize">
                                    {{ result.type }}
                                </v-chip>
                            </template>
                        </v-list-item>
                    </v-list>
                </div>
                <div v-else-if="searchTerm && !isLoading" class="pa-8 text-center text-grey">
                    No results found for "{{ searchTerm }}"
                </div>
                <div v-else-if="!searchTerm" class="pa-8 text-center text-grey-lighten-1">
                    Start typing to search...
                </div>
            </v-card>
        </div>
    </v-overlay>
</template>

<style scoped>
.search-overlay :deep(.v-overlay__scrim) {
    background: rgba(15, 23, 42, 0.8);
    backdrop-filter: blur(4px);
}

.search-wrapper {
    width: 90vw;
    max-width: 650px;
    margin: 0 auto;
}

.search-card {
    border-radius: 16px;
    overflow: hidden;
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.search-field :deep(.v-field__input) {
    font-size: 1.1rem;
}

.results-container {
    max-height: 450px;
    overflow-y: auto;
}

.result-item {
    transition: background-color 0.2s;
    cursor: pointer;
}

.result-item:hover {
    background-color: rgba(var(--v-theme-primary), 0.05);
}

.filter-row {
    background: rgba(var(--v-theme-surface-variant), 0.2);
    border-bottom: 1px solid rgba(var(--v-border-color), 0.05);
}

.filter-chip {
    transition: all 0.2s ease-in-out;
}

.filter-chip:not(.filter-chip--selected) {
    opacity: 0.6;
    border-color: rgba(var(--v-border-color), 0.15) !important;
}

.filter-chip:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    opacity: 0.9;
}

.filter-chip--selected {
    opacity: 1 !important;
    font-weight: 600;
}
</style>